import type {
  OrgSettings,
  OwnAppCheck,
  OwnAppCredentialKind,
  OwnAppSetup,
  OwnAppVerifyResult,
} from "@kontaktfix/shared";
import { Badge, Button, Card, cn, Field, Input, Skeleton, Spinner, Tabs } from "@kontaktfix/ui";
import {
  ArrowLeftIcon,
  ArrowSquareOutIcon,
  CheckCircleIcon,
  CheckIcon,
  CircleIcon,
  CopyIcon,
  DownloadSimpleIcon,
  KeyIcon,
  ShieldCheckIcon,
  WarningIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Page, PageHeader } from "@/components/page";
import { api } from "@/lib/api";
import { toastError } from "@/lib/errors";
import { formatDate } from "@/lib/i18n";
import { keys, useOwnAppSetup } from "@/lib/queries";

const PERMISSIONS = [
  { key: "contacts", name: "Contacts.ReadWrite" },
  { key: "users", name: "User.Read.All" },
  { key: "groups", name: "GroupMember.Read.All" },
] as const;
/** Optional: instant group change notifications (issue #31). Without it groups are checked every 10 minutes. */
const SCRIPT_ROLES = [...PERMISSIONS.map((p) => p.name), "Group.Read.All"];

const ENTRA_APP_REGISTRATIONS =
  "https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade";

/** Wait between automatic re-checks while Microsoft applies freshly granted permissions. */
const RETRY_SECONDS = 15;
const MAX_RETRIES = 12;

function setupScript(setup: OwnAppSetup, kind: OwnAppCredentialKind): string {
  const lines = [
    `# Kontaktfix: creates the app registration for "${setup.orgName}" in your Microsoft Entra tenant.`,
    "# Run in PowerShell 7 as Global Administrator (or Privileged Role Administrator).",
    "# First time only: Install-Module Microsoft.Graph.Applications -Scope CurrentUser",
    '$ErrorActionPreference = "Stop"',
    `$TenantId = "${setup.tenantId}"`,
    "",
    'Connect-MgGraph -TenantId $TenantId -Scopes "Application.ReadWrite.All","AppRoleAssignment.ReadWrite.All" -NoWelcome',
    "",
    "# Microsoft Graph and the application permissions Kontaktfix uses",
    "$graph = Get-MgServicePrincipal -Filter \"appId eq '00000003-0000-0000-c000-000000000000'\"",
    `$roles = ${SCRIPT_ROLES.map((name) => `"${name}"`).join(",")} | ForEach-Object {`,
    "  $name = $_",
    '  $graph.AppRoles | Where-Object { $_.Value -eq $name -and $_.AllowedMemberTypes -contains "Application" }',
    "}",
    "",
    '$app = New-MgApplication -DisplayName "Kontaktfix" -SignInAudience "AzureADMyOrg" `',
    '  -RequiredResourceAccess @(@{ ResourceAppId = $graph.AppId; ResourceAccess = @($roles | ForEach-Object { @{ Id = $_.Id; Type = "Role" } }) })' +
      (kind === "certificate" ? " `" : ""),
    ...(kind === "certificate"
      ? [
          `  -KeyCredentials @(@{ Type = "AsymmetricX509Cert"; Usage = "Verify"; DisplayName = "Kontaktfix"; Key = [Convert]::FromBase64String("${setup.certificate.base64}") })`,
        ]
      : []),
    "$sp = New-MgServicePrincipal -AppId $app.AppId",
    "",
    "# Grant admin consent (retries while the new app replicates in Entra)",
    "foreach ($role in $roles) {",
    "  for ($i = 1; ; $i++) {",
    "    try {",
    "      New-MgServicePrincipalAppRoleAssignment -ServicePrincipalId $sp.Id -PrincipalId $sp.Id -ResourceId $graph.Id -AppRoleId $role.Id | Out-Null",
    "      break",
    "    } catch {",
    "      if ($i -ge 6) { throw }",
    "      Start-Sleep -Seconds 10",
    "    }",
    "  }",
    "}",
    ...(kind === "secret"
      ? [
          "",
          '$secret = Add-MgApplicationPassword -ApplicationId $app.Id -PasswordCredential @{ DisplayName = "Kontaktfix"; EndDateTime = (Get-Date).AddMonths(24) }',
        ]
      : []),
    "",
    'Write-Host ""',
    'Write-Host "Done. Paste into Kontaktfix:" -ForegroundColor Green',
    'Write-Host "Application (client) ID: $($app.AppId)"',
    ...(kind === "secret" ? ['Write-Host "Client secret:           $($secret.SecretText)"'] : []),
  ];
  return `${lines.join("\n")}\n`;
}

export function download(filename: string, content: string, type: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function CopyButton({ value, label }: { value: string; label?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Button size={label ? "sm" : "icon-sm"} variant="ghost" onClick={copy} aria-label={t("common.copy")}>
      {copied ? <CheckIcon className="text-success" /> : <CopyIcon />}
      {label}
    </Button>
  );
}

/** A value to copy, styled like a DNS record value on Resend. */
export function CopyValue({ value }: { value: string }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-md border border-border bg-subtle py-0.5 pr-0.5 pl-2 font-mono text-[12px]">
      <span className="truncate">{value}</span>
      <CopyButton value={value} />
    </span>
  );
}

function SetupStep({
  index,
  done,
  title,
  description,
  children,
}: {
  index: number;
  done?: boolean;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <div className="flex items-start gap-4 border-b border-border px-5 py-4">
        <span
          className={cn(
            "flex size-7 shrink-0 items-center justify-center rounded-full border text-[12px] font-semibold",
            done ? "border-success bg-success text-white" : "border-border-strong text-fg-muted",
          )}
        >
          {done ? <CheckIcon className="size-4" strokeWidth={3} /> : index}
        </span>
        <div className="min-w-0">
          <h3 className="text-[14px] font-semibold">{title}</h3>
          {description && <p className="mt-0.5 text-[13px] text-fg-muted">{description}</p>}
        </div>
      </div>
      <div className="p-5">{children}</div>
    </Card>
  );
}

function KindOption({
  selected,
  onSelect,
  title,
  text,
  badge,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  text: string;
  badge?: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex flex-1 gap-3 rounded-lg border p-4 text-left transition-colors",
        selected
          ? "border-accent bg-accent-soft/50 ring-3 ring-accent/10"
          : "border-border hover:border-border-strong",
      )}
    >
      <span className={cn("mt-0.5 [&_svg]:size-4", selected ? "text-accent" : "text-fg-subtle")}>
        {selected ? <CheckCircleIcon /> : <CircleIcon />}
      </span>
      <span>
        <span className="flex items-center gap-2 text-[13.5px] font-medium">
          {title}
          {badge && <Badge tone="accent">{badge}</Badge>}
        </span>
        <span className="mt-0.5 block text-[12.5px] text-fg-muted">{text}</span>
      </span>
    </button>
  );
}

function ManualSteps({ setup, kind }: { setup: OwnAppSetup; kind: OwnAppCredentialKind }) {
  const { t } = useTranslation();
  const item = (n: number, content: ReactNode) => (
    <li className="flex gap-3">
      <span className="mt-px flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-fg-muted">
        {n}
      </span>
      <div className="min-w-0 flex-1 text-[13px]">{content}</div>
    </li>
  );
  return (
    <ol className="flex flex-col gap-5">
      {item(
        1,
        <>
          <p>{t("ownApp.manual.register")}</p>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12.5px] text-fg-muted">
            <span className="flex items-center gap-2">
              {t("ownApp.manual.name")} <CopyValue value="Kontaktfix" />
            </span>
            <span>{t("ownApp.manual.accountTypes")}</span>
          </div>
          <Button size="sm" className="mt-3" asChild>
            <a href={ENTRA_APP_REGISTRATIONS} target="_blank" rel="noreferrer">
              <ArrowSquareOutIcon />
              {t("ownApp.manual.openEntra")}
            </a>
          </Button>
        </>,
      )}
      {item(
        2,
        <>
          <p>{t("ownApp.manual.permissions")}</p>
          <div className="mt-3 overflow-hidden rounded-md border border-border">
            <table className="w-full text-[12.5px]">
              <thead className="bg-subtle text-left text-fg-muted">
                <tr>
                  <th className="px-3 py-2 font-medium">{t("ownApp.manual.permission")}</th>
                  <th className="px-3 py-2 font-medium">{t("ownApp.manual.type")}</th>
                  <th className="hidden px-3 py-2 font-medium sm:table-cell">{t("ownApp.manual.purpose")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {PERMISSIONS.map((p) => (
                  <tr key={p.key}>
                    <td className="px-3 py-1.5">
                      <CopyValue value={p.name} />
                    </td>
                    <td className="px-3 py-1.5 text-fg-muted">{t("ownApp.manual.application")}</td>
                    <td className="hidden px-3 py-1.5 text-fg-muted sm:table-cell">
                      {t(`ownApp.purpose.${p.key}`)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>,
      )}
      {item(3, <p>{t("ownApp.manual.grant")}</p>)}
      {item(
        4,
        kind === "certificate" ? (
          <>
            <p>{t("ownApp.manual.uploadCert")}</p>
            <Button size="sm" className="mt-3" asChild>
              <a href="/api/connection/own-app/certificate.cer" download="kontaktfix.cer">
                <DownloadSimpleIcon />
                {t("ownApp.downloadCert")}
              </a>
            </Button>
            <p className="mt-2 font-mono text-[11.5px] text-fg-subtle">
              {t("ownApp.thumbprint")}: {setup.certificate.thumbprint}
            </p>
          </>
        ) : (
          <p>{t("ownApp.manual.createSecret")}</p>
        ),
      )}
      {item(5, <p>{t("ownApp.manual.copyId")}</p>)}
    </ol>
  );
}

function CheckBadge({ check, running }: { check: OwnAppCheck | null; running: boolean }) {
  const { t } = useTranslation();
  const status = running ? "running" : (check?.status ?? "waiting");
  const badge = {
    running: (
      <Badge>
        <Spinner className="size-3" />
        {t("ownApp.check.running")}
      </Badge>
    ),
    waiting: <Badge>{t("ownApp.check.waiting")}</Badge>,
    skipped: <Badge>{t("ownApp.check.waiting")}</Badge>,
    ok: (
      <Badge tone="success">
        <CheckCircleIcon className="size-3" />
        {t("ownApp.check.ok")}
      </Badge>
    ),
    failed: (
      <Badge tone="danger">
        <XCircleIcon className="size-3" />
        {t("ownApp.check.failed")}
      </Badge>
    ),
  }[status];
  return badge;
}

export function ConnectOwnAppPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const client = useQueryClient();
  const { data: setup, error } = useOwnAppSetup();
  const [kind, setKind] = useState<OwnAppCredentialKind>("certificate");
  const [way, setWay] = useState<"script" | "manual">("script");
  const [clientId, setClientId] = useState("");
  const [secret, setSecret] = useState("");
  const [result, setResult] = useState<OwnAppVerifyResult | null>(null);
  const [running, setRunning] = useState(false);
  const [retry, setRetry] = useState<{ attempt: number; seconds: number } | null>(null);

  useEffect(() => {
    if (setup?.active) {
      setKind(setup.active.kind);
      setClientId(setup.active.clientId);
    }
  }, [setup?.active]);
  const verify = useCallback(
    async (attempt = 0) => {
      setRetry(null);
      setRunning(true);
      try {
        const res = await api.post<OwnAppVerifyResult>("/api/connection/own-app/verify", {
          clientId: clientId.trim(),
          kind,
          ...(kind === "secret" && secret.trim() ? { secret: secret.trim() } : {}),
        });
        setResult(res);
        if (res.ok && res.settings) {
          client.setQueryData<OrgSettings>(keys.settings, res.settings);
          await client.invalidateQueries({ queryKey: keys.me });
          await client.invalidateQueries({ queryKey: keys.ownApp });
          setSecret("");
          toast.success(t("ownApp.connected"));
          return;
        }
        // Only the permissions are missing: Microsoft may still be applying the admin consent.
        const waitingForMicrosoft =
          res.checks[0]?.status === "ok" &&
          res.checks.every(
            (c) => c.status === "ok" || c.reason === "not_granted" || c.reason === "not_effective",
          );
        if (waitingForMicrosoft && attempt < MAX_RETRIES) {
          setRetry({ attempt: attempt + 1, seconds: RETRY_SECONDS });
        }
      } catch (err) {
        toastError(err);
      } finally {
        setRunning(false);
      }
    },
    [client, clientId, kind, secret, t],
  );

  // Countdown to the next automatic check; leaving the page cancels it.
  useEffect(() => {
    if (!retry) return;
    if (retry.seconds <= 0) {
      void verify(retry.attempt);
      return;
    }
    const id = setTimeout(() => setRetry((r) => r && { ...r, seconds: r.seconds - 1 }), 1000);
    return () => clearTimeout(id);
  }, [retry, verify]);

  const clientIdValid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    clientId.trim(),
  );
  const keepsStoredSecret =
    kind === "secret" && setup?.active?.kind === "secret" && setup.active.clientId === clientId.trim();
  const canVerify = clientIdValid && (kind === "certificate" || secret.trim() !== "" || keepsStoredSecret);
  const connected = Boolean(result?.ok);
  const checkFor = (key: string) => result?.checks.find((c) => c.key === key) ?? null;

  const back = (
    <Link
      to="/settings/{-$tab}"
      params={{ tab: "microsoft" }}
      className="inline-flex items-center gap-1.5 text-[12.5px] text-fg-muted hover:text-fg"
    >
      <ArrowLeftIcon className="size-3.5" />
      {t("ownApp.back")}
    </Link>
  );

  if (error) {
    return (
      <Page className="max-w-[860px]">
        <PageHeader eyebrow={back} title={t("ownApp.title")} />
        <Card className="p-5 text-[13px] text-warning">{t("ownApp.unavailable")}</Card>
      </Page>
    );
  }

  return (
    <Page className="max-w-[860px]">
      <PageHeader
        eyebrow={back}
        title={t("ownApp.title")}
        description={t("ownApp.description")}
        actions={
          connected || setup?.active ? (
            <Badge tone="success">
              <CheckCircleIcon className="size-3" />
              {t("ownApp.status.connected")}
            </Badge>
          ) : (
            <Badge>{t("ownApp.status.notConnected")}</Badge>
          )
        }
      />
      {!setup ? (
        <Skeleton className="h-96" />
      ) : (
        <div className="flex flex-col gap-5">
          <SetupStep
            index={1}
            done={connected}
            title={t("ownApp.step1.title")}
            description={t("ownApp.step1.text")}
          >
            <div className="flex flex-col gap-3 md:flex-row">
              <KindOption
                selected={kind === "certificate"}
                onSelect={() => setKind("certificate")}
                title={t("ownApp.kind.certificate")}
                text={t("ownApp.kind.certificateText", { date: formatDate(setup.certificate.expiresAt) })}
                badge={t("ownApp.recommended")}
              />
              <KindOption
                selected={kind === "secret"}
                onSelect={() => setKind("secret")}
                title={t("ownApp.kind.secret")}
                text={t("ownApp.kind.secretText")}
              />
            </div>
          </SetupStep>

          <SetupStep
            index={2}
            done={connected}
            title={t("ownApp.step2.title")}
            description={t("ownApp.step2.text")}
          >
            <Tabs
              className="mb-4"
              value={way}
              onValueChange={(v) => setWay(v as "script" | "manual")}
              tabs={[
                { value: "script", label: t("ownApp.way.script") },
                { value: "manual", label: t("ownApp.way.manual") },
              ]}
            />
            {way === "script" ? (
              <div className="flex flex-col gap-3">
                <p className="text-[13px] text-fg-muted">{t("ownApp.script.text")}</p>
                <div className="overflow-hidden rounded-md border border-border">
                  <div className="flex items-center justify-between border-b border-border bg-subtle px-3 py-1.5">
                    <span className="font-mono text-[11.5px] text-fg-muted">kontaktfix-setup.ps1</span>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          download("kontaktfix-setup.ps1", setupScript(setup, kind), "text/plain")
                        }
                      >
                        <DownloadSimpleIcon />
                        {t("ownApp.script.download")}
                      </Button>
                      <CopyButton value={setupScript(setup, kind)} label={t("common.copy")} />
                    </div>
                  </div>
                  <pre className="max-h-64 overflow-auto p-3 font-mono text-[11.5px] leading-relaxed text-fg-muted">
                    {setupScript(setup, kind).replace(
                      setup.certificate.base64,
                      `${setup.certificate.base64.slice(0, 24)}…`,
                    )}
                  </pre>
                </div>
                <p className="text-[12px] text-fg-subtle">
                  {kind === "certificate" ? t("ownApp.script.outputCert") : t("ownApp.script.outputSecret")}
                </p>
              </div>
            ) : (
              <ManualSteps setup={setup} kind={kind} />
            )}
          </SetupStep>

          <SetupStep
            index={3}
            done={connected}
            title={t("ownApp.step3.title")}
            description={t("ownApp.step3.text")}
          >
            <form
              className="flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (canVerify) void verify();
              }}
            >
              <Field label={t("ownApp.tenantId")} hint={t("ownApp.tenantIdHint")}>
                <CopyValue value={setup.tenantId} />
              </Field>
              <Field label={t("ownApp.clientId")} htmlFor="own-client-id">
                <Input
                  id="own-client-id"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  className="max-w-md font-mono"
                  autoComplete="off"
                  spellCheck={false}
                  aria-invalid={clientId.trim() !== "" && !clientIdValid}
                />
              </Field>
              {kind === "secret" && (
                <Field
                  label={t("ownApp.secret")}
                  htmlFor="own-secret"
                  hint={
                    keepsStoredSecret
                      ? t("ownApp.secretKeep", { hint: setup.active?.secretHint ?? "" })
                      : t("ownApp.secretHint")
                  }
                >
                  <Input
                    id="own-secret"
                    type="password"
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    className="max-w-md font-mono"
                    autoComplete="off"
                    icon={<KeyIcon />}
                  />
                </Field>
              )}
              <div className="flex flex-wrap items-center gap-3">
                <Button type="submit" variant="primary" loading={running} disabled={!canVerify}>
                  <ShieldCheckIcon />
                  {setup.active ? t("ownApp.verifyAgain") : t("ownApp.verify")}
                </Button>
                {retry && (
                  <span className="flex items-center gap-2 text-[12.5px] text-fg-muted">
                    {t("ownApp.retrying", { seconds: retry.seconds })}
                    <button
                      type="button"
                      className="underline hover:text-fg"
                      onClick={() => verify(retry.attempt)}
                    >
                      {t("ownApp.checkNow")}
                    </button>
                  </span>
                )}
              </div>

              {(result || running) && (
                <div className="overflow-hidden rounded-md border border-border">
                  <table className="w-full text-[12.5px]">
                    <thead className="bg-subtle text-left text-fg-muted">
                      <tr>
                        <th className="px-3 py-2 font-medium">{t("ownApp.check.title")}</th>
                        <th className="w-32 px-3 py-2 text-right font-medium">{t("ownApp.check.status")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {(["credential", ...PERMISSIONS.map((p) => p.key)] as const).map((key) => {
                        const check = checkFor(key);
                        const permission = PERMISSIONS.find((p) => p.key === key);
                        return (
                          <tr key={key} className="align-top">
                            <td className="px-3 py-2.5">
                              <div className="font-medium">
                                {permission ? (
                                  <span className="font-mono">{permission.name}</span>
                                ) : (
                                  t(`ownApp.check.credential.${kind}`)
                                )}
                              </div>
                              {check?.status === "failed" && check.reason && (
                                <div className="mt-1 flex items-start gap-1.5 text-danger">
                                  <WarningIcon className="mt-0.5 size-3.5 shrink-0" />
                                  <span className="break-words">
                                    {t(`ownApp.reason.${check.reason}`, {
                                      defaultValue: t("ownApp.reason.unknown"),
                                    })}
                                    {check.detail && (
                                      <span className="mt-0.5 block font-mono text-[11px] text-fg-subtle">
                                        {check.detail}
                                      </span>
                                    )}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-right">
                              <CheckBadge check={check} running={running} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {connected && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-success-soft px-4 py-3 text-[13px] text-success">
                  <span className="flex items-center gap-2 font-medium">
                    <CheckCircleIcon className="size-4" />
                    {t("ownApp.connected")}
                  </span>
                  <Button
                    size="sm"
                    onClick={() => navigate({ to: "/settings/{-$tab}", params: { tab: "microsoft" } })}
                  >
                    {t("ownApp.done")}
                  </Button>
                </div>
              )}
            </form>
          </SetupStep>
        </div>
      )}
    </Page>
  );
}
